<script setup lang="ts">
import type { ShipexkursTab } from '~~/types';
import type { WikiVehicle } from '~~/types/gamedata';

const props = defineProps({
  raw: {
    type: Object as () => WikiVehicle,
    required: true,
  },
});

type PerformanceStat = {
  name: string;
  max: number;
  suffix: string;
  value: number;
  boostedValue: number;
};

// Nicht jedes Schiff hat alle Kennzahlen (z.B. kein Schild oder Frachtraster)
// — daher werden fehlende Werte herausgefiltert statt sie als 0 zu werten,
// damit das Radar je Schiff eine unterschiedliche Achsenzahl (3, 5, 6, ...)
// haben kann. Maxima orientieren sich an den bereits in
// `sidebar-performance.vue` verwendeten Skalen (Geschwindigkeit/Agilität).
// `getBoosted` ist optional — fehlt eine geboostete Variante (z.B. Hülle,
// Schild, Fracht), zeigt die zweite Serie denselben Wert wie die erste.

// ===PERFORMANCE_STAT_DEFS===
const PERFORMANCE_STAT_DEFS: {
  name: string;
  max: number;
  suffix: string;
  get: (raw: WikiVehicle) => number | null | undefined;
  getBoosted?: (raw: WikiVehicle) => number | null | undefined;
}[] = [
  { name: "Rollen", max: 300, suffix: "°/Sek.", get: (raw) => raw.agility?.roll, getBoosted: (raw) => raw.agility?.roll_boosted },
  { name: "Nicken", max: 100, suffix: "°/Sek.", get: (raw) => raw.agility?.pitch, getBoosted: (raw) => raw.agility?.pitch_boosted },
  { name: "Gieren", max: 100, suffix: "°/Sek.", get: (raw) => raw.agility?.yaw, getBoosted: (raw) => raw.agility?.yaw_boosted },
];

const performanceStats = computed<PerformanceStat[]>(() =>
  PERFORMANCE_STAT_DEFS
    .map((def) => {
      const value = def.get(props.raw);
      if (value == null) return null;
      const boostedValue = def.getBoosted?.(props.raw) ?? value;
      return { name: def.name, max: def.max, suffix: def.suffix, value, boostedValue };
    })
    .filter((stat): stat is PerformanceStat => stat !== null),
);

const performanceIndicators = computed(() =>
  performanceStats.value.map((stat) => ({ name: stat.name, max: stat.max, suffix: stat.suffix })),
);

const performanceSeries = computed(() => [
  {
    name: "Normal",
    values: performanceStats.value.map((stat) => stat.value),
    color: "primary",
  },
  {
    name: "Geboostet",
    values: performanceStats.value.map((stat) => stat.boostedValue),
    color: "secondary",
  },
]);

type BoostMultiplierStat = {
  name: string;
  max: number;
  suffix: string;
  value: number;
};

// Nicht in `types/gamedata.ts` typisiert (kommt aus `WikiItem`s Catch-all
// `[key: string]: unknown`) — hier nur die tatsächlich genutzten Felder.
type FlightControllerBoostAxis = { positive?: number | null; negative?: number | null } | null;
type FlightController = {
  boost_multiplier?: {
    accel_x?: FlightControllerBoostAxis;
    accel_y?: FlightControllerBoostAxis;
    accel_z?: FlightControllerBoostAxis;
    pitch?: number | null;
    yaw?: number | null;
    roll?: number | null;
    pitch_accel?: number | null;
    yaw_accel?: number | null;
    roll_accel?: number | null;
  } | null;
  boost_activation?: {
    pre_delay_time?: number | null;
    ramp_up_time?: number | null;
    ramp_down_time?: number | null;
  } | null;
  /** Boost-"Treibstoff" (Afterburner-Kapazitor) — Regeneration, Verzögerung und Verbrauchsrate. */
  boost_capacitor?: {
    capacity?: number | null;
    regen_per_sec?: number | null;
    regen_delay?: number | null;
    regen_time?: number | null;
    idle_cost?: number | null;
  } | null;
};

const flightController = computed(
  () =>
    props.raw.ports?.find((port) => port.name === "hardpoint_controller_flight")?.equipped_item
      ?.flight_controller as FlightController | undefined,
);

// `boost_multiplier`-Werte sind kleine Faktoren (~1.0–1.6), keine Grad/Sek.-
// Werte wie die Agility-Stats oben — eigene Skala und Suffix.
const PERFORMANCE_BOOST_MULT_DEFS: {
  name: string;
  max: number;
  suffix: string;
  get: (flightController: FlightController | undefined) => number | null | undefined;
}[] = [
  { name: "Vorwärts", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_y?.positive },
  { name: "Rückwärts", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_y?.negative },
  { name: "Strafe (Hoch)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_z?.positive },
  { name: "Strafe (Runter)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_z?.negative },
  { name: "Strafe (Links/Rechts)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.accel_x?.positive },
  { name: "Neigen", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.pitch },
  { name: "Gieren", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.yaw },
  { name: "Rollen", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.roll },
  { name: "Neigen (Beschl.)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.pitch_accel },
  { name: "Gieren (Beschl.)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.yaw_accel },
  { name: "Rollen (Beschl.)", max: 2, suffix: "x", get: (flightController) => flightController?.boost_multiplier?.roll_accel },
];

const performanceBoostMultiplierStats = computed<BoostMultiplierStat[]>(() =>
  PERFORMANCE_BOOST_MULT_DEFS
    .map((def) => {
      const value = def.get(flightController.value);
      if (value == null) return null;
      return { name: def.name, max: def.max, suffix: def.suffix, value };
    })
    .filter((stat): stat is BoostMultiplierStat => stat !== null),
);

const performanceBoostMultiplierIndicators = computed(() =>
  performanceBoostMultiplierStats.value.map((stat) => ({ name: stat.name, max: stat.max, suffix: stat.suffix })),
);

const performanceBoostMultiplierSeries = computed(() => [{
  name: "Boost-Multiplikator",
  values: performanceBoostMultiplierStats.value.map((stat) => stat.value),
  color: "primary",
}]);

function formatNumber(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits }).format(value);
}

interface MiscStat {
  label: string;
  value: string;
  unit?: string;
}

// Geschwindigkeits-Graph, vormals in einer eigenen Sidebar-Karte
// (`sidebar-speed-ship.vue`, entfernt) — jetzt Teil des Performance-Tabs.
const speedTabs = computed<ShipexkursTab[]>(() => [{
  label: "Geschwindigkeit",
  icon: "i-lucide-gauge",
  items: [{
    label: "Höchstgeschwindigkeit (m/Sek.)",
    graph: "circle",
    full: true,
    values: [{
      label: "SCM",
      value: Number(props.raw.speed?.scm ?? 0),
      min: 0,
      max: 1800,
      color: "primary",
    }, {
      label: "SCM Boosted",
      value: Number(props.raw.speed?.boost_forward ?? 0),
      min: 0,
      max: 1800,
      color: "secondary",
    }, {
      label: "NAV",
      value: Number(props.raw.speed?.max ?? 0),
      min: 0,
      max: 1800,
      color: "error",
    }],
  }, {
    // Selbe Rotationsdaten wie im Radar-Chart oben ("Schiffsachsen
    // Rotationsgeschwindigkeit") — als Ring-Graph zusätzlich, nicht ersetzend,
    // für eine zweite Darstellung derselben Werte.
    label: "Rollen (°/Sek.)",
    third: true,
    graph: "circle",
    values: [{
      label: "Normal",
      value: props.raw.agility?.roll ?? 0,
      min: 0,
      max: 300,
    }, {
      label: "Boosted",
      value: props.raw.agility?.roll_boosted ?? 0,
      min: 0,
      max: 300,
      color: "error",
    }],
  }, {
    label: "Neigen (°/Sek.)",
    third: true,
    graph: "circle",
    values: [{
      label: "Normal",
      value: props.raw.agility?.pitch ?? 0,
      min: 0,
      max: 100,
    }, {
      label: "Boosted",
      value: props.raw.agility?.pitch_boosted ?? 0,
      min: 0,
      max: 100,
      color: "error",
    }],
  }, {
    label: "Gieren (°/Sek.)",
    third: true,
    graph: "circle",
    values: [{
      label: "Normal",
      value: props.raw.agility?.yaw ?? 0,
      min: 0,
      max: 100,
    }, {
      label: "Boosted",
      value: props.raw.agility?.yaw_boosted ?? 0,
      min: 0,
      max: 100,
      color: "error",
    }],
  }],
}]);

// Ramp Up/Down: Ein-/Ausklingzeit des Afterburners bis zur vollen
// Schubleistung — sitzt am selben `flight_controller`-Item wie die
// Boost-Multiplikatoren oben (`boost_activation` statt `boost_multiplier`).
const rampStats = computed<MiscStat[]>(() => {
  const activation = flightController.value?.boost_activation;
  const stats: MiscStat[] = [];
  if (activation?.ramp_up_time != null) stats.push({ label: "Ramp-Up", value: formatNumber(activation.ramp_up_time), unit: "s" });
  if (activation?.ramp_down_time != null) stats.push({ label: "Ramp-Down", value: formatNumber(activation.ramp_down_time), unit: "s" });
  if (activation?.pre_delay_time != null) stats.push({ label: "Aktivierungsverzögerung", value: formatNumber(activation.pre_delay_time), unit: "s" });
  return stats;
});

// Boost-"Treibstoff" (Afterburner-Kapazitor): Regeneration, Verzögerung bis
// die Regeneration startet, und Zeit bis der Kapazitor bei Dauerboost leer
// ist (Kapazität / Verbrauch pro Sekunde im Ruhezustand `idle_cost`).
const boostCapacitorStats = computed<MiscStat[]>(() => {
  const capacitor = flightController.value?.boost_capacitor;
  const stats: MiscStat[] = [];
  if (capacitor?.regen_per_sec != null) stats.push({ label: "Regeneration", value: formatNumber(capacitor.regen_per_sec), unit: "/s" });
  if (capacitor?.regen_delay != null) stats.push({ label: "Regen-Verzögerung", value: formatNumber(capacitor.regen_delay), unit: "s" });
  if (capacitor?.capacity != null && capacitor.idle_cost) {
    stats.push({ label: "Deplete", value: formatNumber(capacitor.capacity / capacitor.idle_cost), unit: "s" });
  }
  return stats;
});

// Triebwerks-Performance: G-Kraft je Triebwerkstyp — ein Balken pro Typ,
// eine Farbe (Magnitude-Vergleich über Kategorien, keine Identität mehrerer
// Serien nötig, siehe dataviz-Vorgabe "sequential = one hue").
const THRUSTER_TYPE_LABELS: Record<string, string> = {
  Main: "Haupt",
  Maneuver: "Manöver",
  Retro: "Retro",
  Vtol: "VTOL",
  VTOL: "VTOL",
};

const thrusterStats = computed(() =>
  (props.raw.propulsion?.thrusters ?? [])
    .filter((thruster) => thruster.g != null)
    .map((thruster) => ({ label: THRUSTER_TYPE_LABELS[thruster.type] ?? thruster.type, g: thruster.g! })),
);

// Für flächige Balken (anders als dünne Linien/15%-Flächen im Radar-Chart)
// markiert die dataviz-Palettenvalidierung die App-Standardfarben
// (aris-400/industrial-400, sehr hell) als zu hell für den Lightness-Band-
// Check grosser Flächen — diese dunkleren Stufen derselben Farbfamilie
// bestehen `validate_palette.js "#009189,#c34b1c" --mode dark` sauber.
const BAR_COLOR_PRIMARY = "#009189";
const BAR_COLOR_SECONDARY = "#c34b1c";

const thrusterGForceCategories = computed(() => thrusterStats.value.map((stat) => stat.label));
const thrusterGForceSeries = computed(() => [
  { name: "G-Kraft", values: thrusterStats.value.map((stat) => stat.g), color: BAR_COLOR_PRIMARY },
]);

// Beschleunigung & Bremsung: zwei Gruppen (Beschleunigen/Bremsen) über feste
// Farben unterschieden — echte Identität (zwei unterschiedliche Vorgänge),
// deshalb zwei Serien statt einer, mit `null` an der jeweils anderen
// Kategorie (ECharts lässt den Balken dort schlicht aus).
type AccelGroup = "accel" | "brake";
const ACCEL_DEFS: { label: string; group: AccelGroup; get: (raw: WikiVehicle) => number | null | undefined }[] = [
  { label: "0 → SCM", group: "accel", get: (raw) => raw.speed?.zero_to_scm },
  { label: "0 → Max", group: "accel", get: (raw) => raw.speed?.zero_to_max },
  { label: "SCM → 0", group: "brake", get: (raw) => raw.speed?.scm_to_zero },
  { label: "Max → 0", group: "brake", get: (raw) => raw.speed?.max_to_zero },
];

const accelStats = computed(() =>
  ACCEL_DEFS
    .map((def) => {
      const value = def.get(props.raw);
      return value == null ? null : { label: def.label, group: def.group, value };
    })
    .filter((stat): stat is { label: string; group: AccelGroup; value: number } => stat !== null),
);

const accelCategories = computed(() => accelStats.value.map((stat) => stat.label));
const accelSeries = computed(() => [
  {
    name: "Beschleunigung",
    values: accelStats.value.map((stat) => (stat.group === "accel" ? stat.value : null)),
    color: BAR_COLOR_PRIMARY,
  },
  {
    name: "Bremsung",
    values: accelStats.value.map((stat) => (stat.group === "brake" ? stat.value : null)),
    color: BAR_COLOR_SECONDARY,
  },
]);

// Treibstoff-Performance: Hydrogen-Tankkapazität (`raw.fuel`) und
// Quantentreibstoff (`raw.quantum`, bereits für die Bodenfahrzeug-Variante in
// `main-tabs-performance-ground.vue` genutzt).
const fuelStats = computed<MiscStat[]>(() => {
  const fuel = props.raw.fuel;
  const stats: MiscStat[] = [];
  if (fuel?.capacity != null) stats.push({ label: "Tankkapazität", value: formatNumber(fuel.capacity), unit: "SCU" });
  return stats;
});

// Treibstofffluss je Triebwerkstyp + Treibstoffeinlass: Verbrauch pro
// Triebwerk = Schubkraft (N) / 10.000 × Verbrauch je 10.000 N — je Rolle
// (Haupt/Retro/VTOL/Manöver) über alle verbauten Triebwerke aufsummiert.
const fuelFlowStats = computed(() => {
  const totals = new Map<string, number>();

  for (const port of props.raw.ports ?? []) {
    if (port.category_label !== "Thrusters") continue;
    const thruster = port.equipped_item?.thruster;
    const thrust = thruster?.thrust_capacity;
    const burnRate = thruster?.fuel_burn_per_10k_newton;
    if (thrust == null || burnRate == null) continue;

    const label = THRUSTER_TYPE_LABELS[thruster?.role ?? "Maneuver"] ?? (thruster?.role ?? "Sonstige");
    const flow = (thrust / 10_000) * burnRate;
    totals.set(label, (totals.get(label) ?? 0) + flow);
  }

  const stats = [...totals.entries()].map(([label, value]) => ({ label, value }));
  if (props.raw.fuel?.intake_rate != null) stats.push({ label: "Treibstoffeinlass", value: props.raw.fuel.intake_rate });
  return stats;
});

const fuelFlowCategories = computed(() => fuelFlowStats.value.map((stat) => stat.label));
const fuelFlowSeries = computed(() => [
  { name: "Verbrauch", values: fuelFlowStats.value.map((stat) => stat.value), color: BAR_COLOR_PRIMARY },
]);

const quantumFuelStats = computed<MiscStat[]>(() => {
  const quantum = props.raw.quantum;
  const stats: MiscStat[] = [];
  if (quantum?.quantum_fuel_capacity != null) stats.push({ label: "Quantentreibstoffkapazität", value: formatNumber(quantum.quantum_fuel_capacity), unit: "SCU" });
  if (quantum?.quantum_range != null) stats.push({ label: "Reichweite", value: formatNumber(quantum.quantum_range / 1_000_000_000), unit: "Gm" });
  if (quantum?.quantum_speed != null) stats.push({ label: "Quantengeschwindigkeit", value: formatNumber(quantum.quantum_speed / 1_000_000), unit: "Mm/s" });
  if (quantum?.quantum_spool_time != null) stats.push({ label: "Spool-Zeit", value: formatNumber(quantum.quantum_spool_time), unit: "s" });
  return stats;
});
</script>

<template>
  <div>
    <UTabs
      orientation="vertical"
      variant="link"
      class="ml-2"
      size="sm"
      default-value="flight"
      :items="[
        { label: 'Flug-Performance', value: 'flight', slot: 'flight' },
        { label: 'Triebwerks-Performance', value: 'engine', slot: 'engine' },
        { label: 'Treibstoff-Performance', value: 'fuel', slot: 'fuel' }]"
      :ui="{
        root: 'items-start',
        list: 'space-y-1',
        trigger: 'border border-default data-[state=active]:border-primary data-[state=active]:bg-white/5',
      }"
    >
      <template #flight>
        <!--
          Container Query statt sm:/lg: — der Slider sitzt in einer
          resizable USplitter-Spalte, deren Breite unabhängig vom Viewport
          ist (siehe gleiches Muster in `ship/table/tab-content.vue`).
          Unterhalb `@lg` (32rem Containerbreite) stehen die Charts
          untereinander, ab `@lg` nebeneinander, jeweils halbbreit.
        -->
        <div class="@container mx-auto w-full space-y-4">
          <ShipexkursShipTable :tabs="speedTabs" />
          <div class="flex flex-wrap gap-4">
            <ShipexkursPerformanceFrame class="w-full @lg:w-[calc(50%-0.5rem)]">
              <template #header>
                <ShipexkursSystemIcon name="maneuveringthruster" class="size-4 text-aris-400" />
                Schiffsachsen Rotationsgeschwindigkeit
              </template>
              <ChartsRadarChart
                v-if="performanceStats.length >= 3"
                :height="260"
                :indicators="performanceIndicators"
                :series="performanceSeries"
              />
              <p v-else class="px-4 py-6 text-sm text-neutral-500">Keine ausreichenden Leistungsdaten verfügbar.</p>
            </ShipexkursPerformanceFrame>
            <ShipexkursPerformanceFrame class="w-full @lg:w-[calc(50%-0.5rem)]">
              <template #header>
                Boostmultiplikatoren
              </template>
              <ChartsRadarChart
                v-if="performanceBoostMultiplierStats.length >= 3"
                :height="260"
                :indicators="performanceBoostMultiplierIndicators"
                :series="performanceBoostMultiplierSeries"
              />
              <p v-else class="px-4 py-6 text-sm text-neutral-500">Keine ausreichenden Leistungsdaten verfügbar.</p>
            </ShipexkursPerformanceFrame>
          </div>
        </div>
      </template>

      <template #engine>
        <div class="@container mx-auto w-full space-y-4">
          <ShipexkursPerformanceFrame v-if="rampStats.length">
            <template #header>Ramp-Verhalten</template>
            <div class="grid grid-cols-3 gap-4">
              <ShipexkursStatHero
                v-for="stat in rampStats"
                :key="stat.label"
                :label="stat.label"
                :value="stat.value"
                :unit="stat.unit"
              />
            </div>
          </ShipexkursPerformanceFrame>

          <ShipexkursPerformanceFrame v-if="boostCapacitorStats.length">
            <template #header>
              <ShipexkursSystemIcon name="mainthruster" class="size-4 text-aris-400" />
              Boost-Kapazitor
            </template>
            <div class="grid grid-cols-3 gap-4">
              <ShipexkursStatHero
                v-for="stat in boostCapacitorStats"
                :key="stat.label"
                :label="stat.label"
                :value="stat.value"
                :unit="stat.unit"
              />
            </div>
          </ShipexkursPerformanceFrame>

          <div v-if="thrusterGForceCategories.length || accelCategories.length" class="flex flex-wrap gap-4">
            <ShipexkursPerformanceFrame v-if="thrusterGForceCategories.length" class="w-full @lg:w-[calc(50%-0.5rem)]">
              <template #header>
                <ShipexkursSystemIcon name="mainthruster" class="size-4 text-aris-400" />
                G-Kraft nach Triebwerkstyp
              </template>
              <ChartsBarChart :height="240" :categories="thrusterGForceCategories" :series="thrusterGForceSeries" unit="g" />
            </ShipexkursPerformanceFrame>
            <ShipexkursPerformanceFrame v-if="accelCategories.length" class="w-full @lg:w-[calc(50%-0.5rem)]">
              <template #header>Beschleunigung & Bremsung</template>
              <ChartsBarChart :height="240" :categories="accelCategories" :series="accelSeries" unit="s" />
            </ShipexkursPerformanceFrame>
          </div>
          <p v-if="!thrusterGForceCategories.length && !accelCategories.length && !rampStats.length && !boostCapacitorStats.length" class="px-4 py-6 text-sm text-neutral-500">Keine Triebwerksdaten verfügbar.</p>
        </div>
      </template>

      <template #fuel>
        <div class="@container mx-auto w-full space-y-4">
          <ShipexkursPerformanceFrame v-if="fuelStats.length">
            <template #header>
              <ShipexkursSystemIcon name="fueltank" class="size-4 text-aris-400" />
              Tankkapazität
            </template>
            <div class="grid grid-cols-2 gap-4">
              <ShipexkursStatHero
                v-for="stat in fuelStats"
                :key="stat.label"
                :label="stat.label"
                :value="stat.value"
                :unit="stat.unit"
              />
            </div>
          </ShipexkursPerformanceFrame>

          <ShipexkursPerformanceFrame v-if="fuelFlowCategories.length">
            <template #header>
              <ShipexkursSystemIcon name="fueltank" class="size-4 text-aris-400" />
              Treibstoffverbrauch (SCU/min)
            </template>
            <ChartsBarChart :height="220" :categories="fuelFlowCategories" :series="fuelFlowSeries" unit="SCU/min" horizontal />
          </ShipexkursPerformanceFrame>

          <ShipexkursPerformanceFrame v-if="quantumFuelStats.length">
            <template #header>
              <ShipexkursSystemIcon name="quantumfueltank" class="size-4 text-aris-400" />
              Quantentreibstoff
            </template>
            <div class="grid grid-cols-2 gap-4">
              <ShipexkursStatHero
                v-for="stat in quantumFuelStats"
                :key="stat.label"
                :label="stat.label"
                :value="stat.value"
                :unit="stat.unit"
              />
            </div>
          </ShipexkursPerformanceFrame>
          <p v-if="!fuelStats.length && !fuelFlowCategories.length && !quantumFuelStats.length" class="px-4 py-6 text-sm text-neutral-500">Keine Treibstoffdaten verfügbar.</p>
        </div>
      </template>
    </UTabs>
  </div>
</template>
